import "@/App.css";
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Dashboard from "@/pages/Dashboard";
import BusinessProfile from "@/pages/BusinessProfile";
import Offers from "@/pages/Offers";
import Billing from "@/pages/Billing";
import Analytics from "@/pages/Analytics";
import Admin from "@/pages/Admin";
import PublicPage from "@/pages/PublicPage";
import OfferDetail from "@/pages/OfferDetail";
import OrderStatus from "@/pages/OrderStatus";
import Orders from "@/pages/Orders";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";

// Hostnames that serve the DealWisp app itself. Any other host is treated as a customer's custom domain.
const MAIN_HOSTS = (process.env.REACT_APP_MAIN_HOSTS || "dealwisp.online,www.dealwisp.online,app.dealwisp.online")
  .split(",").map((h) => h.trim().toLowerCase());
const isMainHost = (h) => MAIN_HOSTS.includes(h) || h === "localhost" || h === "127.0.0.1" || h.endsWith(".local");

function CustomDomainRouter({ host }) {
  const [slug, setSlug] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.get(`/public/by-domain/${host}`).then((r) => setSlug(r.data.slug)).catch(() => setFailed(true));
  }, [host]);
  if (failed) return <Landing />;
  if (!slug) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  return (
    <Routes>
      <Route path="/" element={<PublicPage slugOverride={slug} />} />
      <Route path="/offer/:offerId" element={<OfferDetail slugOverride={slug} />} />
      <Route path="/order/:orderId" element={<OrderStatus slugOverride={slug} />} />
      <Route path="/b/:slug/order/:orderId" element={<OrderStatus />} />
      <Route path="/b/:slug" element={<PublicPage />} />
      <Route path="/b/:slug/offer/:offerId" element={<OfferDetail />} />
      <Route path="*" element={<PublicPage slugOverride={slug} />} />
    </Routes>
  );
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/b/:slug" element={<PublicPage />} />
      <Route path="/b/:slug/offer/:offerId" element={<OfferDetail />} />
      <Route path="/b/:slug/order/:orderId" element={<OrderStatus />} />
      <Route path="/dashboard/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/dashboard/business" element={<ProtectedRoute><BusinessProfile /></ProtectedRoute>} />
      <Route path="/dashboard/offers" element={<ProtectedRoute><Offers /></ProtectedRoute>} />
      <Route path="/dashboard/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/dashboard/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App grain">
      <BrowserRouter>
        <AuthProvider>
          {isMainHost(window.location.hostname.toLowerCase()) ? <AppRouter /> : <CustomDomainRouter host={window.location.hostname.toLowerCase()} />}
          <Toaster position="top-right" theme="dark" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
